import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Typography, Space, Alert, message } from "antd";
import { MailOutlined, SendOutlined } from "@ant-design/icons";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

/**
 * Modal "Enviar SDK" - envia al dev externo el gameKey + instrucciones
 * del SDK Educa360 Live por email (AWS SES + nodemailer del backend).
 *
 * Props:
 *   - open: bool
 *   - game: object | null
 *   - onClose()
 *   - onSend(email, customMessage) -> Promise (lanza si falla)
 */
export function SendSdkModal({ open, game, onClose, onSend }) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  if (!game) return null;

  const handleSend = async () => {
    try {
      const { email, customMessage } = await form.validateFields();
      setSubmitting(true);
      try {
        const result = await onSend(email.trim(), (customMessage || "").trim());
        message.success(
          `Email enviado a ${result.to}. El dev recibira el gameKey y las instrucciones.`,
        );
        onClose();
      } catch (e) {
        message.error(e?.message || "Error enviando email");
      } finally {
        setSubmitting(false);
      }
    } catch {
      // antd validation error, ya muestra el feedback
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={
        <Space>
          <MailOutlined />
          <span>Enviar SDK al desarrollador</span>
        </Space>
      }
      width={580}
      onOk={handleSend}
      okText={
        <Space size={6}>
          <SendOutlined />
          <span>Enviar</span>
        </Space>
      }
      cancelText="Cancelar"
      confirmLoading={submitting}
      destroyOnClose
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Alert
          type="info"
          showIcon
          message={
            <span>
              Se enviara un email con el gameKey{" "}
              <Text code>{game.gameKey}</Text> y las 5 instrucciones de
              integracion del SDK Unity. Puedes enviarlo a varios devs cuantas
              veces necesites.
            </span>
          }
        />

        <Form form={form} layout="vertical">
          <Form.Item
            name="email"
            label="Email del destinatario"
            rules={[
              { required: true, message: "Email requerido" },
              { type: "email", message: "Formato de email invalido" },
              { max: 254, message: "Maximo 254 chars" },
            ]}
          >
            <Input placeholder="dev@empresa.com" autoFocus autoComplete="off" />
          </Form.Item>

          <Form.Item
            name="customMessage"
            label="Mensaje personalizado (opcional)"
            rules={[{ max: 1000, message: "Maximo 1000 chars" }]}
            extra="Se mostrara al principio del email, antes de las instrucciones. Util para indicar contexto del proyecto o fecha de entrega."
          >
            <TextArea
              rows={4}
              placeholder="Hola Pablo, esto es para el proyecto VR Mindfulness que arrancamos manyana. Cualquier duda, escribeme."
              showCount
              maxLength={1000}
            />
          </Form.Item>
        </Form>

        <Paragraph style={{ fontSize: 12, color: "#888", marginBottom: 0 }}>
          Remitente:{" "}
          <Text code style={{ fontSize: 12 }}>
            info@educajob.net
          </Text>
        </Paragraph>
      </Space>
    </Modal>
  );
}
